@if (sample(); as sample) {
  <div class="npdc-title">
    <hgroup>
      <h1>{{ sample.content.lithology }}</h1>
      <sub>[ {{ sample.content.sampleName }} ]</sub>
      @if (sample.content.draft) {
        <p>
          <br />
          <mat-chip>Draft</mat-chip>
        </p>
      }
    </hgroup>
    <div class="npdc-title__actions">
      @if (
        authService.user() &&
        (permissions()?.mayUpdate ||
          authService.canInvoke(authService.endpoints.editGeologicalSample))
      ) {
        <button mat-flat-button class="npdc-toolbar-button" [routerLink]="'edit'">Edit</button>
      }
    </div>
  </div>

  @if (sample.content.geojson; as geometry) {
    <p>
      <mat-card appearance="outlined" class="npdc-leaflet">
        <app-leaflet-group [geometry]="geometry"></app-leaflet-group>
      </mat-card>

      @if (coords.length === 2 || coords.length === 3) {
        <sub> <strong>Lat: </strong>{{ coords[1] }}, <strong>Lng: </strong>{{ coords[0] }} </sub>
      }

      @if (sample.content.positionAccuracy) {
        <br />
        <sub> <strong>Accuracy: </strong> {{ sample.content.positionAccuracy }} </sub>
      }
    </p>
  }

  @if (sample.content.description) {
    <p></p>
    <h2>Description</h2>
    <div class="npdc-markdown" [innerHtml]="sample.content.description | markdown"></div>
    <p></p>
  }

  @if (!imgList.error() && imgList.value()) {
    <div class="npdc-card-grid">
      @for (img of this.imgList.value()?.items; track img.id) {
        <mat-card>
          <a
            mat-card-image
            title="Click for hi-res image"
            href="{{ baseAddr }}{{ img.id }}/_blob"
            target="_blank"
          >
            <app-card-image
              mat-card-image
              alt="{{ img.title || img.filename }} thumbnail"
              src="{{ baseAddr }}{{ img.id }}/_blob"
            />
          </a>
          <mat-card-header>
            <mat-card-title>{{ img.title || img.filename }}</mat-card-title>
          </mat-card-header>
          <mat-card-content></mat-card-content>
        </mat-card>
      }
    </div>

    @if (imgCount() > defaultImgPageSize) {
      <mat-paginator
        showFirstLastButtons
        (page)="onImgPage($event)"
        [length]="imgCount()"
        [pageIndex]="imgPage()"
        [pageSize]="imgPageSize()"
        [pageSizeOptions]="imgPageOptions"
      />
    }
  }

  @if (
    sample.content.geologists ||
    sample.content.cabinetRef ||
    sample.content.thinSectionRef ||
    sample.content.originalSampleName ||
    sample.content.collectedYear ||
    sample.content.expedition ||
    sample.content.fossil ||
    sample.content.placename
  ) {
    <h2>Details</h2>

    @if (sample.content.geologists) {
      @for (geologist of sample.content.geologists; track geologist) {
        <mat-list-item>
          <mat-icon matListItemIcon>person</mat-icon>
          <span matListItemTitle>Geologist</span>
          <span matListItemLine>{{ geologist }}</span>
        </mat-list-item>
      }
    }

    @if (sample.content.collectedYear) {
      <mat-list-item>
        <mat-icon matListItemIcon>event</mat-icon>
        <span matListItemTitle>Collection year</span>
        <span matListItemLine>{{ sample.content.collectedYear }}</span>
      </mat-list-item>
    }

    @if (sample.content.placename) {
      <mat-list-item>
        <mat-icon matListItemIcon>globe_location_pin</mat-icon>
        <span matListItemTitle>Placename</span>
        <span matListItemLine>{{ sample.content.placename }}</span>
      </mat-list-item>
    }

    @if (sample.content.expedition) {
      <mat-list-item>
        <mat-icon matListItemIcon>hiking</mat-icon>
        <span matListItemTitle>Expedition</span>
        <span matListItemLine>{{ sample.content.expedition }}</span>
      </mat-list-item>
    }

    @if (sample.content.originalSampleName) {
      <mat-list-item>
        <mat-icon matListItemIcon>barcode</mat-icon>
        <span matListItemTitle>Original sample name</span>
        <span matListItemLine>{{ sample.content.originalSampleName }}</span>
      </mat-list-item>
    }

    @if (sample.content.cabinetRef) {
      <mat-list-item>
        <mat-icon matListItemIcon>shelves</mat-icon>
        <span matListItemTitle>Cabinet reference</span>
        <span matListItemLine>{{ sample.content.cabinetRef }}</span>
      </mat-list-item>
    }

    @if (sample.content.thinSectionRef) {
      <mat-list-item>
        <mat-icon matListItemIcon>carpenter</mat-icon>
        <span matListItemTitle>Thin section reference</span>
        <span matListItemLine>{{ sample.content.thinSectionRef }}</span>
      </mat-list-item>
    }

    @if (sample.content.fossil) {
      <mat-list-item>
        <mat-icon matListItemIcon>tibia</mat-icon>
        <span matListItemTitle>Fossil</span>
        <span matListItemLine>{{ sample.content.fossil ? 'yes' : 'no' }}</span>
      </mat-list-item>
    }
  }

  @if (sample.content.links) {
    <h2>Links</h2>
    @for (link of sample.content.links; track link) {
      <a appStopPropagation href="{{ link.href }}" target="_blank" rel="nofollow">
        <mat-list-item>
          <mat-icon matListItemIcon>link</mat-icon>
          <span matListItemTitle>{{ link.title || link.href }}</span>
          <span matListItemLine><b>Relation: </b>{{ link.rel }}</span>
        </mat-list-item>
      </a>
    }
  }

  @if (!fileList.error() && fileList.value()?.items?.length) {
    <h2>Files</h2>
    <app-list [resource]="fileList">
      <mat-nav-list>
        @for (file of fileList.value()?.items; track file.id; let isLast = $last) {
          <a mat-list-item target="_blank" href="{{ baseAddr }}{{ file.id }}/_blob">
            <mat-icon matListItemIcon>files</mat-icon>
            <span matListItemTitle>{{ file.title || file.filename }}</span>
            <span matListItemLine>
              @if (file.mimeType) {
                <b>Content type: </b>{{ file.mimeType }}
              }
            </span>
            <span matListItemLine>
              @if (file.mimeType) {
                <b>Size: </b>{{ file.byteSize | humanReadableSize }}
              }
            </span>
          </a>
        }
      </mat-nav-list>

      @if (fileCount() > defaultFilePageSize) {
        <mat-paginator
          showFirstLastButtons
          (page)="onFilePage($event)"
          [length]="fileCount()"
          [pageIndex]="filePage()"
          [pageSize]="filePageSize()"
          [pageSizeOptions]="filePageOptions"
        />
      }
    </app-list>
  }

  <h2>Other</h2>
  <mat-list-item>
    <span matListItemTitle>Created</span>
    <span matListItemLine>{{ sample.created }}</span>
  </mat-list-item>
  <mat-list-item>
    <span matListItemTitle>Modified</span>
    <span matListItemLine>{{ sample.modified }}</span>
  </mat-list-item>
}
